import { StreakStatusResponse, StreakResponseV2, AUTOMATIC_STREAK_STATUS } from "@cede/types";
import { ServerApiError } from "@cede/types";
import { isServerApiError, isServerApiRfcError, Rfc9457ProblemDetails, QueryKeys } from "@cede/utils";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getAutomaticStreakStatus, getAutomaticStreakStatusV2 } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

/**
 * Unified streak response type that can hold either V1 or V2 streak data.
 * Use the `version` field to discriminate between the two.
 */
export type UnifiedStreakResponse = StreakStatusResponse | StreakResponseV2;

export interface UseStreakStatusOptions {
  streakId: string | null;
  enabled?: boolean;
  refetchInterval?: number | false;
  /**
   * Version of the streak API to use. Defaults to 1 for backward compatibility.
   * Set to 2 for V2 streaks (volume-based instead of cycle-based).
   */
  version?: 1 | 2;
}

// Helper to determine refetch interval based on streak status
function getRefetchIntervalForStatus(status?: string): number | false {
  if (!status) return false;

  // Active streaks should poll more frequently
  if (status === AUTOMATIC_STREAK_STATUS.PENDING || status === AUTOMATIC_STREAK_STATUS.IN_PROGRESS) {
    return 5000; // 5 seconds for active streaks
  }

  // Terminal states don't need polling
  return false;
}

export const useStreakStatus = ({ streakId, enabled = true, refetchInterval, version = 1 }: UseStreakStatusOptions) => {
  const { auth, authenticated } = useHubUser();

  const query = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAK, streakId, version],
    queryFn: async (): Promise<UnifiedStreakResponse | null> => {
      if (!streakId) return null;

      // Use version-specific API
      const result =
        version === 2
          ? await getAutomaticStreakStatusV2(auth, streakId)
          : await getAutomaticStreakStatus(auth, streakId);

      if (isServerApiError(result)) {
        throw new Error((result as ServerApiError).message);
      }
      if (isServerApiRfcError(result)) {
        throw new Error((result as Rfc9457ProblemDetails).detail);
      }

      return result as UnifiedStreakResponse;
    },
    enabled: enabled && authenticated && !!streakId,
    refetchInterval: (query) => {
      if (refetchInterval !== undefined) return refetchInterval;
      return getRefetchIntervalForStatus(query.state.data?.status);
    },
    staleTime: 5000, // 5 seconds
  });

  return {
    streak: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error?.message,
    refetch: query.refetch,
  };
};
